import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../AppContext";
import PaymentService from "../services/payment.service";
import { useTranslation } from "react-i18next";
import moment from "moment";
import LoadPage from "../common/LoadPage";
import AlertMessage from "../common/AlertMessage";
import useAxiosFetch from "../common/useAxiosFetch";

const FeatureUsageBar = ({ label, current, limit, resetsAt }) => {
  const { t } = useTranslation();
  
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
  const isNearLimit = percentage >= 80 && percentage < 95;
  const isAtLimit = percentage >= 95;
  
  const getBarColor = () => {
    if (isUnlimited) return 'var(--green-risk-score)';
    if (isAtLimit) return 'var(--red-risk-score)';
    if (isNearLimit) return 'var(--yellow-risk-score)';
    return 'var(--green-risk-score)';
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontWeight: '500' }}>{label}</span>
        <span style={{ fontWeight: 'bold', color: getBarColor() }}>
          {current} / {isUnlimited ? t('Unlimited') : limit}
        </span>
      </div>
      {!isUnlimited && (
        <>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            backgroundColor: '#e9ecef', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${percentage}%`, 
              height: '100%', 
              backgroundColor: getBarColor(),
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ fontSize: '0.85em', color: '#666', marginTop: '3px' }}>
            {percentage.toFixed(0)}% {t('used')}
            {resetsAt && (
              <span style={{ marginLeft: '10px' }}>
                • {t('Resets')}: {moment(resetsAt).format('MMM D, YYYY')}
              </span>
            )}
          </div>
        </>
      )}
      {isUnlimited && (
        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '3px' }}>
          ∞ {t('Unlimited')}
        </div>
      )}
    </div>
  );
};

const SubscriptionsPage = () => {
  const { currentUser, alertMessage, showAlertMessage } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  
  // Fetch feature limits using useAxiosFetch
  const { data: featureLimits } = useAxiosFetch("/api/features/limits");

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        // Fetch subscription status
        const status = await PaymentService.getSubscriptionStatus();
        setSubscriptionInfo(status);

        // If there's an active subscription plan, fetch its details
        if (status.subscriptionPlan && status.subscriptionPlan.planId) {
          const planResponse = await PaymentService.getPlanById(status.subscriptionPlan.planId);
          setCurrentPlan(planResponse);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching subscription data:", err);
        showAlertMessage('error', t('Error loading subscription information'));
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchSubscriptionData();
    }
  }, [currentUser, t, showAlertMessage]);

  const getSubscriptionStatus = () => {
    if (!subscriptionInfo) return null;

    const now = new Date().getTime();
    
    // Subscription takes priority over trial
    const hasActiveSubscription = subscriptionInfo.subscriptionExpires && subscriptionInfo.subscriptionExpires > now;
    const hasActiveTrial = !hasActiveSubscription && subscriptionInfo.trialExpires && subscriptionInfo.trialExpires > now;

    // Active subscription
    if (hasActiveSubscription) {
      return {
        type: 'subscription',
        status: 'active',
        expiresDate: subscriptionInfo.subscriptionExpires,
        message: t('subscription_active')
      };
    }

    // Active trial
    if (hasActiveTrial) {
      return {
        type: 'trial',
        status: 'active',
        expiresDate: subscriptionInfo.trialExpires,
        message: t('trial_active')
      };
    }

    // Check what expired - subscription takes priority in display
    const subscriptionExpired = subscriptionInfo.subscriptionExpires && subscriptionInfo.subscriptionExpires < now;
    const trialExpired = subscriptionInfo.trialExpires && subscriptionInfo.trialExpires < now;

    if (subscriptionExpired) {
      return {
        type: 'subscription',
        status: 'expired',
        expiresDate: subscriptionInfo.subscriptionExpires,
        message: t('subscription_expired_message')
      };
    }

    if (trialExpired) {
      return {
        type: 'trial',
        status: 'expired',
        expiresDate: subscriptionInfo.trialExpires,
        message: t('trial_expired_message')
      };
    }

    return {
      type: 'none',
      status: 'none',
      message: t('no_subscription')
    };
  };

  if (loading) {
    return <LoadPage />;
  }

  // Check if this is a trial environment (trialExpires > 0)
  const isTrialEnvironment = subscriptionInfo?.trialExpires && subscriptionInfo.trialExpires > 0;
  
  // If not a trial environment, show info message
  if (!isTrialEnvironment) {
    return (
      <div id="content">
        <div id="content-title">{t('subscriptions')}</div>
        <div className="clear-both" />
        <div className="content-bubble">
          <div style={{ 
            padding: '30px', 
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <i className="icon-info" style={{ fontSize: '48px', color: '#0056b3', marginBottom: '20px' }} />
            <h3>{t('no_payment_required')}</h3>
            <p style={{ fontSize: '16px', color: '#666' }}>
              {t('This environment was manually created and does not require a subscription.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getSubscriptionStatus();

  return (
    <div id="content">
      <div id="content-title">{t('subscriptions')}</div>

      <div className="clear-both" />

      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      <div className="content-bubble">
        {/* Current Plan Status */}
        <h3>{t('Current Plan')}</h3>
        
        <div style={{ marginBottom: '30px' }}>
          <div style={{ 
            padding: '20px', 
            backgroundColor: statusInfo?.status === 'active' ? '#d4edda' : '#fff3cd',
            borderRadius: '8px',
            border: `1px solid ${statusInfo?.status === 'active' ? '#c3e6cb' : '#ffeaa7'}`,
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <i 
                className={statusInfo?.status === 'active' ? 'icon-check-circle' : 'icon-Risico'} 
                style={{ 
                  fontSize: '24px', 
                  marginRight: '15px',
                  color: statusInfo?.status === 'active' ? 'var(--green-risk-score)' : 'var(--yellow-risk-score)'
                }}
              />
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: '18px' }}>
                  {currentPlan ? currentPlan.name : 
                   featureLimits?.planInfo?.planName ? featureLimits.planInfo.planName :
                   subscriptionInfo?.subscriptionPlan?.planId ? subscriptionInfo.subscriptionPlan.planId.charAt(0).toUpperCase() + subscriptionInfo.subscriptionPlan.planId.slice(1) :
                   statusInfo?.type === 'trial' ? t('Trial') : t('No Active Plan')}
                </strong>
                {/* All plans are yearly now */}
                {subscriptionInfo?.subscriptionPlan?.planId && !['trial', 'free'].includes(subscriptionInfo.subscriptionPlan.planId) && (
                  <span style={{ 
                    marginLeft: '10px', 
                    padding: '4px 8px', 
                    backgroundColor: 'rgba(0,123,255,0.1)',
                    borderRadius: '4px',
                    fontSize: '0.85em',
                    fontWeight: 'normal'
                  }}>
                    {t('Yearly')}
                  </span>
                )}
                <div style={{ color: '#666', marginTop: '5px' }}>
                  {statusInfo?.message}
                </div>
              </div>
            </div>

            {statusInfo?.expiresDate && (
              <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>{statusInfo?.status === 'active' ? t('Expires on') : t('expired_on')}:</span>
                  <strong>{moment(statusInfo.expiresDate).format('LL')}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{statusInfo?.status === 'active' ? t('Expires') : t('Expired')}:</span>
                  <strong>{moment(statusInfo.expiresDate).fromNow()}</strong>
                </div>
              </div>
            )}
          </div>

          {subscriptionInfo?.trialExpires && !subscriptionInfo?.subscriptionExpires && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#e7f3ff', 
              borderRadius: '8px',
              border: '1px solid #bee5eb',
              marginBottom: '20px'
            }}>
              <i className="icon-info" style={{ marginRight: '10px', color: 'var(--text-color)' }} />
              {t('trial_environment_info')}
            </div>
          )}
        </div>

        {/* Feature Usage */}
        {featureLimits && (
          <>
            <h3>{t('Feature Usage')}</h3>
            <div style={{ 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              marginBottom: '30px'
            }}>
              <FeatureUsageBar 
                label={t('organizations')}
                current={featureLimits.usage.organizations.current}
                limit={featureLimits.limits.maxOrganizations}
              />
              <FeatureUsageBar 
                label={t('domains')}
                current={featureLimits.usage.projects.current}
                limit={featureLimits.limits.maxProjects}
              />
              <FeatureUsageBar 
                label={t('users')}
                current={featureLimits.usage.users.current}
                limit={featureLimits.limits.maxUsers}
              />
              <FeatureUsageBar 
                label={t('AI Calls This Week')}
                current={featureLimits.usage.aiCalls.current}
                limit={featureLimits.limits.aiCallsPerWeek}
                resetsAt={featureLimits.usage.aiCalls.resetsAt}
              />
            </div>
          </>
        )}

        {/* Manage Subscription - Only show if there's an action to take */}
        {(() => {
          const now = new Date().getTime();
          const thirtyDaysFromNow = now + (30 * 24 * 60 * 60 * 1000);
          
          // Check if subscription/trial expires within 30 days
          const expiresWithin30Days = (statusInfo?.expiresDate && statusInfo.expiresDate <= thirtyDaysFromNow);
          
          // Get plan ID from multiple sources
          const planId = currentPlan?.planId || featureLimits?.planInfo?.planId || subscriptionInfo?.subscriptionPlan?.planId;
          
          // Show if: not active, expired, expires within 30 days, or on starter plan
          const shouldShowUpgradeButton = 
            statusInfo?.status !== 'active' || 
            statusInfo?.type !== 'subscription' || 
            expiresWithin30Days ||
            planId === 'starter';
          
          // Don't render the block at all if no action is available
          if (!shouldShowUpgradeButton) {
            return null;
          }
          
          return (
            <>
              <h3>{currentPlan ? t('Manage Subscription') : t('Subscribe Now')}</h3>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#fff', 
                borderRadius: '8px',
                border: '1px solid #ddd',
                marginBottom: '20px'
              }}>
                {(currentPlan || subscriptionInfo?.subscriptionPlan?.planId || featureLimits?.planInfo?.planId) && (
                  <div>
                    <p style={{ marginBottom: '15px' }}>
                      {planId === 'starter' ? (
                        <>
                          <i className="icon-arrow-up" style={{ color: 'var(--text-color)', marginRight: '8px' }} />
                          {t('Upgrade to Premium for unlimited organizations, domains, and more AI calls.')}
                        </>
                      ) : expiresWithin30Days ? (
                        <>
                          <i className="icon-clock" style={{ color: 'var(--yellow-risk-score)', marginRight: '8px' }} />
                          {statusInfo?.type === 'trial' 
                            ? t('Your trial is expiring soon. Subscribe now to continue using Risqui.')
                            : t('Your subscription is expiring soon. Extend now to continue using Risqui.')}
                        </>
                      ) : planId === 'premium' ? (
                        <>
                          <i className="icon-check" style={{ color: 'var(--green-risk-score)', marginRight: '8px' }} />
                          {t('You are on the Premium plan with all features unlocked!')}
                        </>
                      ) : null}
                    </p>
                  </div>
                )}

                <button 
                  className="btn btn-outline-primary btn-block login-submit"
                  onClick={() => navigate('/payment')}
                  style={{margin: 0}}
                  
                >
                  <i className="icon-cart" style={{ marginRight: '8px' }} />
                  {planId === 'starter' 
                    ? t('Upgrade Plan')
                    : expiresWithin30Days
                      ? (statusInfo?.type === 'trial' ? t('Subscribe Now') : t('Extend Subscription'))
                      : t('View Plans')}
                </button>

                <div style={{ marginTop: '15px', fontSize: '0.9em', color: '#666', textAlign: 'center' }}>
                  <i className="icon-shield" style={{ marginRight: '5px' }} />
                  {t('Secure payment processed by Mollie')}
                </div>
              </div>
            </>
          );
        })()}

        <div>
          <p style={{ textAlign: 'center', color: 'rgb(102, 102, 102)' }}>
            {t('Questions? Contact us at')}{' '}
            <a href="mailto:info@risqui.nl">info@risqui.nl</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsPage;
